import { z } from 'zod';
/**
 * Umgebungsvariablen — fail-closed.
 *
 * Fehlt ein Pflichtwert, bricht der Prozessstart mit einer Meldung ab, die
 * jeden fehlenden Wert einzeln benennt. Es gibt bewusst KEINE Fallback-Werte
 * fuer Secrets: Das Altsystem nutzte ein hartkodiertes JWT-Fallback-Secret und
 * liess die gesamte Admin-API durch, wenn kein Passwort gesetzt war.
 */
const hex64 = /^[0-9a-fA-F]{64}$/;
const schema = z.object({
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
    DATABASE_URL: z
        .string()
        .min(1, 'DATABASE_URL fehlt')
        .startsWith('postgres', 'DATABASE_URL muss eine postgres://-URL sein'),
    DATABASE_MIGRATION_URL: z.string().startsWith('postgres').optional(),
    REDIS_URL: z.string().startsWith('redis').default('redis://localhost:6379'),
    SESSION_SECRET: z.string().min(32, 'SESSION_SECRET muss mindestens 32 Zeichen lang sein'),
    ENCRYPTION_KEY: z
        .string()
        .regex(hex64, 'ENCRYPTION_KEY muss exakt 64 Hex-Zeichen lang sein (= 32 Byte)'),
    BOOTSTRAP_EMAIL: z.string().email().optional(),
    BOOTSTRAP_PASSWORD: z.string().min(12).optional(),
    BOOTSTRAP_DOMAIN: z.string().trim().optional(),
    PROJECT_DOMAIN: z.string().trim().optional(),
    // Ein Prozess, ein Port, ein Origin — wie in v1. Die API liefert auch das
    // Frontend aus, deshalb gibt es keinen getrennten Web-Port.
    APP_URL: z.string().url().default('http://localhost:3000'),
    API_URL: z.string().url().default('http://localhost:3000'),
    CORS_ORIGINS: z.string().default(''),
    API_PORT: z.coerce.number().int().positive().default(3000),
    SESSION_ACCESS_TTL_MINUTES: z.coerce.number().int().positive().default(15),
    SESSION_REFRESH_TTL_DAYS: z.coerce.number().int().positive().default(30),
});
/**
 * Validiert die uebergebene Umgebung. Wirft bei Fehlern eine Exception, deren
 * Meldung ALLE Probleme auflistet — nicht nur das erste, damit ein Setup nicht
 * in mehreren Runden repariert werden muss.
 */
export function parseEnv(source = process.env) {
    const result = schema.safeParse(source);
    if (!result.success) {
        const lines = result.error.issues.map((issue) => {
            const key = issue.path.join('.');
            return `  - ${key}: ${issue.message}`;
        });
        throw new Error([
            'Konfiguration unvollstaendig — Start abgebrochen.',
            '',
            ...lines,
            '',
            'Vorlage: .env.example. Secrets erzeugen mit:',
            "  node -e \"console.log(require('crypto').randomBytes(32).toString('hex'))\"",
        ].join('\n'));
    }
    const env = result.data;
    const extra = env.CORS_ORIGINS.split(',')
        .map((o) => o.trim())
        .filter(Boolean);
    return {
        ...env,
        corsOrigins: [...new Set([env.APP_URL, ...extra])],
        isProduction: env.NODE_ENV === 'production',
    };
}
let cached;
/** Validiert einmalig und liefert danach den zwischengespeicherten Wert. */
export function getEnv() {
    cached ??= parseEnv();
    return cached;
}
/** Nur fuer Tests: verwirft den Cache. */
export function resetEnvCache() {
    cached = undefined;
}
//# sourceMappingURL=env.js.map